import React from 'react';
import { useDashboardFeatureFlags } from '@components';
import { Dashboard, PageLayout } from '@components/common';
import { SearchBar } from '@components/common/SearchBar';
import { DashboardPermissionGuard } from '@components/utils/DashboardPermissions';
import { Banner, Button, ButtonGroup, HeadContent, Pagination, ToolbarButton, useMediaQueryContext, useModalContext, } from '@fourthwall/components';
import { PlusIcon } from '@fourthwall/icons';
import { useCreateProduct } from '../Product/hooks/useCreateProduct';
import { useSearchQuery } from '../hooks/useSearchQuery';
import { AddSubscriptionModal, UpgradeSubscriptionBanner, useSubscription, } from '../modules/Subscriptions';
import { useTopSubscriptionPlan } from '../modules/Subscriptions/hooks/useTopSubscriptionPlan';
import { ProductsEmptyState } from './components/ProductsEmptyState';
import { ProductsEmptyStateWithRecommendations } from './components/ProductsEmptyStateWithRecommendations';
import { useFilterProducts } from './hooks/useFilterProducts';
import { ProductsData } from './modules/ProductsData';
import { ProductsFilter } from './modules/ProductsFilter/ProductsFilter';
import * as S from './styled';
export const ProductsView = ({ products, isLoading, totalPages, totalProducts, currentPage, onPageChange, onProductPositionChange, onProductPositionSet, onDuplicateOffer, onArchiveOffer, onUnarchiveOffer, onArchiveManyOffers, onToggleMultipleOfferStatus, }) => {
    const media = useMediaQueryContext();
    const { open } = useModalContext();
    const { searchQuery, searchValue, resetSearchValue, setSearchValue, searchOptions } = useSearchQuery();
    const { hasFilterParams } = useFilterProducts();
    const { openCreateProductModal } = useCreateProduct();
    const { canUpgradeSubscription, offers, subscriptionData } = useSubscription();
    const areSearchOrFiltersParamsApplied = searchQuery || hasFilterParams;
    const { getTopSubscriptionPlanLimitLabel } = useTopSubscriptionPlan();
    const offersLimitLabel = getTopSubscriptionPlanLimitLabel('offers');
    const { dashboardFeatureFlags } = useDashboardFeatureFlags();
    const handleUpgradeSubscription = ({ onSuccess } = {}) => {
        open(AddSubscriptionModal, {
            title: `Upgrade your plan to offer more than ${subscriptionData?.offers.limit} products`,
            source: 'product_list',
            onSuccess,
        });
    };
    const handleCreateProductClick = () => {
        openCreateProductModal();
    };
    const renderSubscriptionBanner = () => {
        if (!offers.hasLimit || !subscriptionData || !dashboardFeatureFlags.subscriptions.enabled)
            return null;
        // Usage < Limit - Threshold
        if (subscriptionData.offers.usage < subscriptionData.offers.limit - offers.threshold) {
            return null;
        }
        // Limit - Threshold < Usage < Limit
        if (subscriptionData.offers.usage >= subscriptionData.offers.limit - offers.threshold &&
            offers.canCreate) {
            if (!canUpgradeSubscription) {
                return (React.createElement(Dashboard.Section.Item, null,
                    React.createElement(Banner, { appearance: "pro" },
                        "You've created ",
                        subscriptionData.offers.usage,
                        " out of ",
                        subscriptionData.offers.limit,
                        ' ',
                        "products allowed on your current plan. To stay within the limit, consider archiving any unused products or",
                        ' ',
                        React.createElement("a", { href: "mailto:support@fourthwall.com" }, "contact our support team"),
                        ".")));
            }
            return (React.createElement(Dashboard.Section.Item, null,
                React.createElement(UpgradeSubscriptionBanner, { source: "product_list", onClick: handleUpgradeSubscription },
                    "You\u2019ve created ",
                    subscriptionData.offers.usage,
                    " out of your",
                    ' ',
                    subscriptionData.offers.limit,
                    "-product limit. Upgrade to",
                    ' ',
                    React.createElement("strong", null, "Fourthwall Pro"),
                    " to offer ",
                    offersLimitLabel,
                    " products.")));
        }
        // Usage >= Limit
        if (!offers.canCreate) {
            if (!canUpgradeSubscription) {
                return (React.createElement(Dashboard.Section.Item, null,
                    React.createElement(Banner, { appearance: "pro" },
                        "You've created ",
                        subscriptionData.offers.usage,
                        " out of ",
                        subscriptionData.offers.limit,
                        ' ',
                        "products allowed on your current plan. To add new products, please archive some existing ones or ",
                        React.createElement("a", { href: "mailto:support@fourthwall.com" }, "contact our support team"),
                        ".")));
            }
            return (React.createElement(Dashboard.Section.Item, null,
                React.createElement(UpgradeSubscriptionBanner, { source: "product_list", onClick: handleUpgradeSubscription },
                    "You\u2019ve hit your ",
                    subscriptionData?.offers.limit,
                    "-product limit. Upgrade to",
                    ' ',
                    React.createElement("strong", null, "Fourthwall Pro"),
                    " to offer ",
                    offersLimitLabel,
                    " products.")));
        }
        return null;
    };
    const renderSearchBar = () => (React.createElement(SearchBar, { name: "search", placeholder: "Search products", value: searchValue, maxLength: searchOptions.maxLength, onValueChange: (value) => setSearchValue(value), onActionIconClick: resetSearchValue }));
    const getToolbarContent = () => {
        if (!media.tablet) {
            return (React.createElement(ButtonGroup, { space: "medium" },
                React.createElement(DashboardPermissionGuard, { permission: "products.create" },
                    React.createElement(ToolbarButton, { label: "Create new product", icon: PlusIcon, onClick: handleCreateProductClick })),
                React.createElement(ProductsFilter, null)));
        }
        return (React.createElement(ButtonGroup, { space: "medium" },
            React.createElement(ProductsFilter, null),
            React.createElement(S.SearchBarWrapper, null, renderSearchBar()),
            React.createElement(DashboardPermissionGuard, { permission: "products.create" },
                React.createElement(Button, { label: "Create new product", appearance: "primary", size: "small", onClick: handleCreateProductClick }))));
    };
    const renderEmptyState = () => {
        if (areSearchOrFiltersParamsApplied) {
            return React.createElement(ProductsEmptyState, null);
        }
        return React.createElement(ProductsEmptyStateWithRecommendations, null);
    };
    return (React.createElement(PageLayout, { size: "large" },
        React.createElement(HeadContent, { title: "Products" }),
        React.createElement(Dashboard.Section, { name: `Products${totalProducts ? ` (${totalProducts})` : ''}`, toolbarContent: getToolbarContent(), bottomContent: !media.tablet && renderSearchBar() },
            renderSubscriptionBanner(),
            products.length > 0 || isLoading ? (React.createElement(React.Fragment, null,
                React.createElement(Dashboard.Section.Item, null,
                    React.createElement(ProductsData, { products: products, currentPage: currentPage, isLoading: isLoading, onArchiveSelectedOffersClick: onArchiveManyOffers, onDuplicateOffer: onDuplicateOffer, onArchiveOffer: onArchiveOffer, onUnarchiveOffer: onUnarchiveOffer, onProductPositionSet: onProductPositionSet, onProductPositionChange: onProductPositionChange, onToggleMultipleOfferStatus: onToggleMultipleOfferStatus })),
                !!totalPages && !!products.length && (React.createElement(Dashboard.Section.Item, null,
                    React.createElement(Pagination, { pageCount: totalPages, currentPage: currentPage, onChange: onPageChange }))))) : (React.createElement(Dashboard.Section.Item, null, renderEmptyState())))));
};
